import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FileText, Mail, Phone, MapPin, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TermsPage() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    da: {
      title: "Handelsbetingelser",
      subtitle: "Vejle AutoExport",
      lastUpdated: "Senest opdateret: 24. juni 2026",
      intro: "Disse handelsbetingelser og salgsvilkår gælder for alle aftaler indgået med Vejle AutoExport (CVR: 30158628) vedrørende vurdering, opkøb og eksport af køretøjer, herunder særligt leasingbiler uden registreringsafgift og moms.",
      sections: [
        {
          title: "1. Generelt",
          body: "Vejle AutoExport driver virksomhed med professionelt opkøb af biler med henblik på eksport. Vi handler primært med leasingselskaber, erhvervsdrivende og private, der ønsker at afvikle leasingbiler uden afgift og moms. Alle aftaler indgås på skriftligt grundlag i form af en digital slutseddel."
        },
        {
          title: "2. Vurdering og Købstilbud",
          body: "Enhver markedsvurdering foretaget via vores hjemmeside eller e-mail er gratis og 100% uforpligtende. Et afgivet købstilbud er gyldigt i 5 hverdage fra afgivelsesdatoen, medmindre andet er skriftligt aftalt. Tilbuddet er baseret på de af kunden afgivne oplysninger om bilens stand, kilometerstand og udstyr."
        },
        {
          title: "3. Kundens Oplysningspligt",
          body: "Kunden garanterer, at alle afgivne oplysninger om køretøjet er korrekte og fyldestgørende. Kunden skal oplyse om eventuelle kendte fejl, mangler, tidligere skader eller udestående gæld/pant i bilen. Såfremt det ved besigtigelse eller afhentning viser sig, at bilens faktiske stand afviger væsentligt fra det oplyste, forbeholder Vejle AutoExport sig retten til at genforhandle eller annullere købstilbuddet."
        },
        {
          title: "4. Afvikling af Leasingaftaler",
          body: "Ved salg af en leasingbil uden afgift og moms indestår kunden (eller leasingselskabet) for, at bilen lovligt kan sælges til eksport. Vejle AutoExport bistår med alt det administrative arbejde i forbindelse med afviklingen over for leasingselskabet, herunder indhentelse af frigivelsesattester og afmeldingsbekræftelser."
        },
        {
          title: "5. Betaling og Overdragelse",
          body: "Betaling for køretøjet sker via sikker bankoverførsel til kundens eller leasingselskabets konto. Pengene overføres i forbindelse med underskrift af slutseddel og overdragelse af bilen. Bilen afhentes kvit og frit af Vejle AutoExport på kundens adresse eller anden aftalt lokation i Danmark. Risikoen for køretøjet overgår til Vejle AutoExport i det øjeblik, bilen afhentes og slutsedlen er underskrevet af begge parter."
        },
        {
          title: "6. Lovvalg og Værneting",
          body: "Enhver tvist, som måtte opstå i forbindelse med en aftale med Vejle AutoExport, skal afgøres efter dansk ret med Retten i Kolding som første instans."
        }
      ],
      backBtn: "Tilbage til forsiden"
    },
    en: {
      title: "Terms & Conditions",
      subtitle: "Vejle AutoExport",
      lastUpdated: "Last updated: June 24, 2026",
      intro: "These terms and conditions apply to all agreements entered into with Vejle AutoExport (CVR: 30158628) regarding the valuation, purchase, and export of vehicles, specifically leasing cars without registration tax and VAT.",
      sections: [
        {
          title: "1. General",
          body: "Vejle AutoExport operates a business purchasing professional vehicles for export. We primarily trade with leasing companies, businesses, and private individuals who want to settle leasing cars without tax and VAT. All agreements are concluded on a written basis in the form of a digital purchase agreement (slutseddel)."
        },
        {
          title: "2. Valuation and Purchase Offer",
          body: "Any market valuation carried out via our website or email is free and 100% non-binding. A submitted purchase offer is valid for 5 business days from the date of issue, unless otherwise agreed in writing. The offer is based on the information provided by the customer regarding the car's condition, mileage, and equipment."
        },
        {
          title: "3. Customer's Duty of Disclosure",
          body: "The customer guarantees that all information provided about the vehicle is correct and complete. The customer must disclose any known faults, defects, previous damage, or outstanding debt/liens on the car. If, upon inspection or pickup, it turns out that the car's actual condition differs significantly from what was reported, Vejle AutoExport reserves the right to renegotiate or cancel the purchase offer."
        },
        {
          title: "4. Settlement of Leasing Agreements",
          body: "When selling a leasing car without tax and VAT, the customer (or leasing company) guarantees that the car can legally be sold for export. Vejle AutoExport assists with all administrative work in connection with the settlement with the leasing company, including obtaining release certificates and deregistration confirmations."
        },
        {
          title: "5. Payment and Transfer",
          body: "Payment for the vehicle is made via secure bank transfer to the customer's or leasing company's account. The funds are transferred in connection with the signing of the purchase agreement and the transfer of the car. The car is picked up free of charge by Vejle AutoExport at the customer's address or another agreed location in Denmark. The risk of the vehicle transfers to Vejle AutoExport the moment the car is picked up and the purchase agreement is signed by both parties."
        },
        {
          title: "6. Governing Law and Jurisdiction",
          body: "Any dispute arising in connection with an agreement with Vejle AutoExport shall be settled in accordance with Danish law, with the Court in Kolding as the court of first instance."
        }
      ],
      backBtn: "Back to home"
    },
    de: {
      title: "Allgemeine Geschäftsbedingungen",
      subtitle: "Vejle AutoExport",
      lastUpdated: "Zuletzt aktualisiert: 24. Juni 2026",
      intro: "Diese Allgemeinen Geschäftsbedingungen gelten für alle mit Vejle AutoExport (CVR: 30158628) geschlossenen Vereinbarungen über die Bewertung, den Ankauf und den Export von Fahrzeugen, insbesondere von Leasingfahrzeugen ohne Zulassungssteuer und Mehrwertsteuer.",
      sections: [
        {
          title: "1. Allgemeines",
          body: "Vejle AutoExport betreibt ein Unternehmen zum Ankauf von gewerblichen Fahrzeugen für den Export. Wir handeln hauptsächlich mit Leasinggesellschaften, Unternehmen und Privatpersonen, die Leasingfahrzeuge ohne Steuer und Mehrwertsteuer abwickeln möchten. Alle Vereinbarungen werden schriftlich in Form eines digitalen Kaufvertrags (slutseddel) geschlossen."
        },
        {
          title: "2. Bewertung und Kaufangebot",
          body: "Jede über unsere Website oder E-Mail durchgeführte Marktbewertung ist kostenlos und 100 % unverbindlich. Ein abgegebenes Kaufangebot ist ab dem Ausstellungsdatum 5 Werktage lang gültig, sofern nicht schriftlich etwas anderes vereinbart wurde. Das Angebot basiert auf den Angaben des Kunden zu Zustand, Kilometerstand und Ausstattung des Fahrzeugs."
        },
        {
          title: "3. Offenlegungspflicht des Kunden",
          body: "Der Kunde garantiert, dass alle Angaben zum Fahrzeug korrekt und vollständig sind. Der Kunde muss alle bekannten Fehler, Mängel, Vorschäden oder ausstehenden Schulden/Pfandrechte am Auto offenlegen. Stellt sich bei der Besichtigung oder Abholung heraus, dass der tatsächliche Zustand des Wagens erheblich von den Angaben abweicht, behält sich Vejle AutoExport das Recht vor, das Kaufangebot neu zu verhandeln oder zu stornieren."
        },
        {
          title: "4. Abwicklung von Leasingverträgen",
          body: "Beim Verkauf eines Leasingfahrzeugs ohne Steuer und Mehrwertsteuer garantiert der Kunde (oder die Leasinggesellschaft), dass das Fahrzeug legal für den Export verkauft werden kann. Vejle AutoExport unterstützt bei allen administrativen Arbeiten im Zusammenhang mit der Abwicklung mit der Leasinggesellschaft, einschließlich der Einholung von Freigabebescheinigungen und Abmeldebestätigungen."
        },
        {
          title: "5. Zahlung und Übergabe",
          body: "Die Bezahlung des Fahrzeugs erfolgt per sicherer Banküberweisung auf das Konto des Kunden oder der Leasinggesellschaft. Die Überweisung erfolgt im Zusammenhang mit der Unterzeichnung des Kaufvertrags und der Übergabe des Fahrzeugs. Das Auto wird von Vejle AutoExport kostenlos an der Adresse des Kunden oder an einem anderen vereinbarten Ort in Dänemark abgeholt. Das Risiko für das Fahrzeug geht in dem Moment auf Vejle AutoExport über, in dem das Auto abgeholt und der Kaufvertrag von beiden Parteien unterzeichnet wird."
        },
        {
          title: "6. Anwendbares Recht und Gerichtsstand",
          body: "Alle Streitigkeiten im Zusammenhang mit einer Vereinbarung mit Vejle AutoExport werden nach dänischem Recht entschieden, wobei das Gericht in Kolding das erstinstanzliche Gericht ist."
        }
      ],
      backBtn: "Zurück zur Startseite"
    }
  };

  const currentContent = content[language] || content.da;

  return (
    <div className="pt-36 md:pt-44 pb-24 bg-slate-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#293e30] font-bold text-xs uppercase tracking-wider mb-8 transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          {currentContent.backBtn}
        </button>

        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-12 space-y-10">
          {/* Header */}
          <div className="border-b border-slate-100 pb-8">
            <div className="flex items-center gap-3 text-[#293e30] mb-3">
              <FileText className="h-8 w-8" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                {currentContent.subtitle}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black italic text-[#293e30] uppercase tracking-tight">
              {currentContent.title}
            </h1>
            <p className="text-xs text-slate-400 mt-2 font-medium">
              {currentContent.lastUpdated}
            </p>
          </div>

          {/* Intro */}
          <p className="text-slate-600 leading-relaxed font-medium text-base">
            {currentContent.intro}
          </p>

          {/* Sections */}
          <div className="space-y-8">
            {currentContent.sections.map((section, idx) => (
              <div key={idx} className="space-y-3">
                <h2 className="text-lg font-bold text-[#293e30] uppercase tracking-wide">
                  {section.title}
                </h2>
                <p className="text-slate-600 leading-relaxed text-sm">
                  {section.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
